// The tag vocabulary from the cross-product UI tagging guideline
// (roadmap/ui-tagging-guideline.md §3). It lives here until the shared
// areas.yaml has an owner (spec D9); keep the two in step when it does.

/** Lowercase levels joined by "-", at most five levels. "_" joins words inside a level. */
export const TAG_PATTERN = /^[a-z0-9_]+(-[a-z0-9_]+){0,4}$/;
export const TAG_MAX_LENGTH = 64;

export function isValidTag(tag: string): boolean {
  return tag.length <= TAG_MAX_LENGTH && TAG_PATTERN.test(tag);
}

/** Global UI chrome that sits outside any page area. */
export const SURFACES = ["sp", "top", "cmd"] as const;

/** Artie's page areas. Navigation elements use these as destination nouns. */
export const AREAS = [
  "chat",
  "projects",
  "skills",
  "knowledge",
  "meetings",
  "plugins",
  "schedules",
  "marketplace",
  "dashboard",
  "publisher",
  "organization",
  "embed_keys",
  "batches",
] as const;

/** Action verbs. Add one only by agreement across products (guideline §3.3). */
export const VERBS = [
  "open", "close", "create", "save", "submit", "cancel", "delete", "edit",
  "approve", "reject", "sign_off", "assign", "upload", "download", "export",
  "import", "search", "filter", "sort", "select", "toggle", "next", "back",
  "send", "print", "copy", "refresh", "expand", "collapse", "rename",
  // Agreed 2026-10-21 (tag coverage spec T12): logging out is an action, not a place.
  "logout",
  // Agreed 2026-10-21: rating a reply and regenerating it are not toggles or refreshes.
  "rate", "regenerate",
] as const;

/** Places a click takes you to that are not page areas (spec T4). */
export const DESTINATIONS = ["account", "settings", "memory"] as const;

/**
 * ARIA roles an untagged click may report as its component (spec T3). Exactly
 * the pipeline's list: any other role string is dropped, because a role
 * attribute is free text and can carry user data.
 */
export const COMPONENT_ROLES = [
  "button", "link", "menuitem", "tab", "checkbox", "switch", "option", "radio", "combobox",
] as const;

const ACTIONS: ReadonlySet<string> = new Set<string>([...VERBS, ...AREAS, ...SURFACES, ...DESTINATIONS]);

/**
 * True when a tag's last level says what the click does (spec T4): a verb,
 * `<verb>_<qualifier>` (`export_md`), or a place — an area, surface or
 * destination. Checked by the coverage test, not at runtime (T6).
 */
export function isValidAction(level: string): boolean {
  if (ACTIONS.has(level)) return true;
  // Match whole verbs as prefixes: a verb can itself contain "_" (sign_off_all).
  return VERBS.some((verb) => level.startsWith(`${verb}_`) && level.length > verb.length + 1);
}
